import { ScanLine, AlertTriangle, Palette } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

/**
 * "How it works" guide (86d3rrfdk) — the adoption path, reopenable from the welcome strip. Three
 * steps + the status vocabulary. No 7-day / multi-day retry wording (the AwaitingNs ladder is gone;
 * waiting is a brief transient).
 */
interface HowItWorksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HowItWorksDialog({ open, onOpenChange }: HowItWorksDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>How Document Capture works</DialogTitle>
          <DialogDescription>Mostly it runs itself. Here’s the whole picture in a minute.</DialogDescription>
        </DialogHeader>

        <ol className="space-y-4">
          <Step icon={ScanLine} title="The machine does the work">
            Scan a Capitol document and drop it in the watched folder. DOP reads its barcode, files it, and
            links it into the matching NetSuite transaction automatically — no clicks, no typing.
          </Step>
          <Step icon={AlertTriangle} title="You handle only the exceptions">
            When something’s ambiguous — no barcode, two numbers, a NetSuite mismatch — it lands in
            <span className="font-medium text-foreground"> Needs review</span>. Everything else is silent and green.
          </Step>
          <Step icon={Palette} title="Status at a glance">
            <ul className="mt-1 space-y-1">
              <Chip tone="success" label="Captured / Linked" desc="read and attached — done" />
              <Chip tone="info" label="Waiting for NetSuite" desc="captured, the transaction just isn’t in NetSuite yet — not an error" />
              <Chip tone="warning" label="Needs review" desc="a person has to decide" />
              <Chip tone="destructive" label="Failed" desc="something to fix, never hidden" />
            </ul>
          </Step>
        </ol>

        <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
          In the review queue: <kbd className="rounded border px-1">↑</kbd>{' '}
          <kbd className="rounded border px-1">↓</kbd> move · <kbd className="rounded border px-1">Enter</kbd> select ·{' '}
          <kbd className="rounded border px-1">Esc</kbd> close.
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Step({ icon: Icon, title, children }: { icon: typeof ScanLine; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{children}</p>
      </div>
    </li>
  );
}

function Chip({ tone, label, desc }: { tone: 'success' | 'info' | 'warning' | 'destructive'; label: string; desc: string }) {
  const toneClass = {
    success: 'bg-success/12 text-success',
    info: 'bg-info/12 text-info',
    warning: 'bg-warning/12 text-warning',
    destructive: 'bg-destructive/12 text-destructive',
  }[tone];
  return (
    <li className="flex items-baseline gap-2">
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${toneClass}`}>{label}</span>
      <span className="text-muted-foreground">{desc}</span>
    </li>
  );
}
